import { useTranslation } from 'react-i18next';

import {
  Typography,
  TYPOGRAPHY_SIZE,
  AutocompleteInput,
  Icon,
  ICONS,
  SelectInput,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';

import { DatevDataTypesEnum } from '@thinknet-gmbh/platform-common';

import { Input } from 'antd';

import { useGetLocationsByCompanyId } from 'src/hooks/companies/get-locations-by-query.hook';

import { YearsButton } from '../years-button';

import { filtersFieldsNameEnum } from '../../../payroll-accounting.constants';

import {
  IPayrollAccountingListFiltersProps,
  IPayrollAccountingFilters,
} from '../../../payroll-accounting.interfaces';

import {
  FiltersContainer,
  FiltersWrapper,
  StyledFilterItem,
} from './payroll-accounting-datev-filters.styles';

interface IDatevNumber {
  datevNumber: string;
  _id: string;
}

interface ILocation {
  datevNumber?: IDatevNumber;
  _id: string;
}

const PayrollAccountingListFilters = ({
  filters,
  companiesOptions,
  handleFilterChange,
  yearsOptions,
  minYear,
  maxYear,
}: IPayrollAccountingListFiltersProps) => {
  const { t } = useTranslation(['payrollAccounting', 'common']);

  const onCompanySelect = (value: string, option: any) => {
    handleFilterChange({
      companyId: option?.key,
      datevNumberId: undefined,
    });
  };

  const onSearch = (value: string) => {
    if (!value) {
      handleFilterChange({
        datevNumberId: undefined,
        companyId: undefined,
      });
    }
  };

  const { locations } = useGetLocationsByCompanyId(filters?.companyId);

  const onSelect = (fieldName: filtersFieldsNameEnum) => (value: string, option: any) => {
    const newFilters = {} as IPayrollAccountingFilters;

    newFilters[fieldName] = option.value;

    handleFilterChange(newFilters);
  };

  const datevOptions = locations?.filter(({ datevNumber }: ILocation) => !!datevNumber);

  return (
    <FiltersWrapper>
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {t('payrollAccounting:labels:tableTitle')}
      </Typography>
      <FiltersContainer>
        <StyledFilterItem>
          <AutocompleteInput
            onSearch={onSearch}
            options={companiesOptions}
            className={'filter-autocomplete'}
            placeholder={t('common:labels.companyName')}
            onSelect={onCompanySelect}
            filterOption={(inputValue, option) => {
              const { value } = option || {};
              return value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
            }}
          >
            <Input
              suffix={
                <Icon icon={ICONS.search} color={PLATFORM_THEME_COLORS.primary.grey} size={22} />
              }
            />
          </AutocompleteInput>
          <SelectInput
            value={filters.datevNumberId}
            placeholder={t('payrollAccounting:labels.clientNumber')}
            onSelect={onSelect(filtersFieldsNameEnum.datevNumberId)}
          >
            {datevOptions?.map(
              ({ datevNumber, _id }: { datevNumber: IDatevNumber; _id: string }) => (
                <SelectInput.Option
                  value={datevNumber?._id}
                  label={datevNumber?.datevNumber}
                  key={`${datevNumber?._id}-${_id}`}
                >
                  № {datevNumber?.datevNumber}
                </SelectInput.Option>
              )
            )}
          </SelectInput>
          <SelectInput
            value={filters?.dataType}
            placeholder={t('payrollAccounting:labels.datatype')}
            onSelect={onSelect(filtersFieldsNameEnum.dataType)}
          >
            <SelectInput.Option
              value={DatevDataTypesEnum.basedata}
              key={DatevDataTypesEnum.basedata}
              title={t('payrollAccounting:labels.datatype')}
            >
              {t(`payrollAccounting:labels.dataTypes.${DatevDataTypesEnum.basedata}`)}
            </SelectInput.Option>
          </SelectInput>
        </StyledFilterItem>
        <StyledFilterItem>
          <YearsButton
            filters={filters}
            minYear={maxYear}
            maxYear={minYear}
            handleFilterChange={handleFilterChange}
          />
          <SelectInput
            className={'year-select'}
            value={filters.year}
            placeholder={t('payrollAccounting:labels.year')}
            onSelect={onSelect(filtersFieldsNameEnum.year)}
          >
            {yearsOptions?.map(year => (
              <SelectInput.Option value={year} label={year} key={year}>
                {year}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </StyledFilterItem>
      </FiltersContainer>
    </FiltersWrapper>
  );
};

export default PayrollAccountingListFilters;
